import { useState, useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { MessageSquare, Send, X, Loader2, User } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { supabase } from "@/integrations/supabase/client";
import { usePlatformConfig } from "@/hooks/usePlatformConfig";
import { motion, AnimatePresence } from "framer-motion";

const PUBLIC_ROUTES = [
  "/", "/funcionalidades", "/integracoes", "/api", "/contato",
  "/ajuda", "/documentacao", "/status", "/termos", "/privacidade",
  "/lojademo", "/tutoriais", "/changelog", "/planos", "/cadastro", "/login",
];

const STORAGE_KEY = "visitor_chat_conv_id";

const PublicChatWidget = () => {
  const { config } = usePlatformConfig();
  const location = useLocation();
  const isPublicRoute = PUBLIC_ROUTES.includes(location.pathname) || location.pathname.startsWith("/parceiro/");
  const [open, setOpen] = useState(false);
  const [showGreeting, setShowGreeting] = useState(false);
  const [messages, setMessages] = useState<any[]>([]);
  const [newMessage, setNewMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [convId, setConvId] = useState<string | null>(null);
  const [visitorName, setVisitorName] = useState("");
  const [nameSubmitted, setNameSubmitted] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const greetingDismissed = useRef(false);

  const isEnabled = (config as any).public_chat_enabled ?? false;
  const welcomeMessage = (config as any).public_chat_welcome_message || "Olá! 👋 Precisa de ajuda? Converse com um de nossos consultores!";

  // Track current page in real-time
  useEffect(() => {
    if (!convId) return;
    supabase.from("visitor_chat_conversations")
      .update({ current_page: location.pathname } as any)
      .eq("id", convId)
      .then();
  }, [location.pathname, convId]);

  // Show greeting after delay
  useEffect(() => {
    if (!isEnabled || open) return;
    const timer = setTimeout(() => {
      if (!greetingDismissed.current) setShowGreeting(true);
    }, 5000);
    return () => clearTimeout(timer);
  }, [isEnabled, open]);

  // Load existing conversation
  useEffect(() => {
    const savedId = localStorage.getItem(STORAGE_KEY);
    if (savedId) {
      setConvId(savedId);
      setNameSubmitted(true);
      loadMessages(savedId);
    }
  }, []);

  // Realtime subscription
  useEffect(() => {
    if (!convId) return;
    const channel = supabase
      .channel("visitor-chat-" + convId)
      .on("postgres_changes", {
        event: "INSERT",
        schema: "public",
        table: "visitor_chat_messages",
        filter: `conversation_id=eq.${convId}`,
      }, (payload) => {
        setMessages(prev => {
          if (prev.some(m => m.id === (payload.new as any).id)) return prev;
          return [...prev, payload.new as any];
        });
      })
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [convId]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const loadMessages = async (id: string) => {
    const { data } = await supabase.rpc("get_visitor_messages", { p_conversation_id: id });
    setMessages((data || []) as any[]);
  };

  const startConversation = async () => {
    if (!visitorName.trim()) return;
    setSending(true);

    // Check if user is authenticated
    const { data: { session } } = await supabase.auth.getSession();

    const { data, error } = await supabase
      .from("visitor_chat_conversations")
      .insert({
        visitor_name: visitorName.trim(),
        current_page: location.pathname,
        user_agent: navigator.userAgent,
        is_authenticated: !!session?.user,
        user_id: session?.user?.id || null,
      } as any)
      .select()
      .single();
    if (!error && data) {
      setConvId(data.id);
      setNameSubmitted(true);
      localStorage.setItem(STORAGE_KEY, data.id);
    }
    setSending(false);
  };

  const sendMessage = async () => {
    if (!newMessage.trim() || !convId) return;
    setSending(true);
    const { error } = await supabase.from("visitor_chat_messages").insert({
      conversation_id: convId,
      sender_type: "visitor",
      content: newMessage.trim(),
    });
    if (!error) setNewMessage("");
    setSending(false);
  };

  const handleOpen = () => {
    setOpen(true);
    setShowGreeting(false);
    greetingDismissed.current = true;
  };

  const dismissGreeting = (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowGreeting(false);
    greetingDismissed.current = true;
  };

  if (!isEnabled || !isPublicRoute) return null;

  return (
    <>
      {/* Greeting bubble */}
      <AnimatePresence>
        {showGreeting && !open && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 10, scale: 0.9 }}
            className="fixed bottom-24 right-6 z-50 max-w-[300px] cursor-pointer"
            onClick={handleOpen}
          >
            <div className="relative bg-card border border-border rounded-2xl rounded-br-sm shadow-xl p-4">
              <button
                onClick={dismissGreeting}
                className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-muted flex items-center justify-center hover:bg-muted-foreground/20 transition-colors"
              >
                <X className="h-3 w-3 text-muted-foreground" />
              </button>
              <p className="text-sm text-foreground">{welcomeMessage}</p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Floating button */}
      <button
        onClick={() => open ? setOpen(false) : handleOpen()}
        className="fixed bottom-6 right-6 z-50 w-14 h-14 rounded-full bg-primary text-primary-foreground shadow-lg flex items-center justify-center hover:scale-105 transition-transform"
      >
        {open ? <X className="h-6 w-6" /> : <MessageSquare className="h-6 w-6" />}
      </button>

      {/* Chat window */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 30, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            className="fixed bottom-24 right-6 z-50 w-[360px] h-[480px] rounded-2xl border border-border bg-card shadow-2xl flex flex-col overflow-hidden"
          >
            {/* Header */}
            <div className="p-4 border-b border-border bg-primary text-primary-foreground">
              <h3 className="font-bold text-sm">{config.site_name || "Atendimento"}</h3>
              <p className="text-[10px] opacity-80">Fale com um consultor</p>
            </div>

            {!nameSubmitted ? (
              <div className="flex-1 flex flex-col items-center justify-center p-6 space-y-4">
                <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
                  <User className="h-8 w-8 text-primary" />
                </div>
                <div className="text-center">
                  <p className="text-sm font-medium text-foreground">Bem-vindo!</p>
                  <p className="text-xs text-muted-foreground mt-1">Informe seu nome para iniciar a conversa</p>
                </div>
                <div className="w-full space-y-2">
                  <Input
                    value={visitorName}
                    onChange={e => setVisitorName(e.target.value)}
                    onKeyDown={e => e.key === "Enter" && startConversation()}
                    placeholder="Seu nome..."
                    className="h-10 text-sm"
                  />
                  <Button
                    onClick={startConversation}
                    disabled={sending || !visitorName.trim()}
                    className="w-full bg-primary text-primary-foreground"
                  >
                    {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : "Iniciar Conversa"}
                  </Button>
                </div>
              </div>
            ) : (
              <>
                <div className="flex-1 overflow-y-auto p-3 space-y-2">
                  {messages.length === 0 && (
                    <div className="flex items-center justify-center h-full text-muted-foreground">
                      <div className="text-center">
                        <MessageSquare className="h-8 w-8 mx-auto mb-2 opacity-20" />
                        <p className="text-xs">Envie sua primeira mensagem!</p>
                        <p className="text-[10px] text-muted-foreground mt-1">Um consultor irá respondê-lo em breve</p>
                      </div>
                    </div>
                  )}
                  {messages.map(msg => (
                    <div key={msg.id} className={`flex ${msg.sender_type === "visitor" ? "justify-end" : "justify-start"}`}>
                      <div className={`max-w-[80%] rounded-xl px-3 py-2 ${
                        msg.sender_type === "visitor"
                          ? "bg-primary text-primary-foreground"
                          : "bg-secondary text-foreground"
                      }`}>
                        <p className="text-xs whitespace-pre-wrap">{msg.content}</p>
                        <p className={`text-[8px] mt-0.5 ${msg.sender_type === "visitor" ? "text-primary-foreground/60" : "text-muted-foreground"}`}>
                          {msg.sender_type === "admin" ? "Consultor" : "Você"} · {new Date(msg.created_at).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })}
                        </p>
                      </div>
                    </div>
                  ))}
                  <div ref={messagesEndRef} />
                </div>

                <div className="p-3 border-t border-border">
                  <div className="flex gap-2">
                    <Input
                      value={newMessage}
                      onChange={e => setNewMessage(e.target.value)}
                      onKeyDown={e => e.key === "Enter" && !e.shiftKey && (e.preventDefault(), sendMessage())}
                      placeholder="Digite sua mensagem..."
                      className="h-9 text-xs"
                    />
                    <Button onClick={sendMessage} disabled={sending || !newMessage.trim()} size="sm" className="h-9 px-3 bg-primary text-primary-foreground">
                      {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                    </Button>
                  </div>
                </div>
              </>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default PublicChatWidget;
